import { useState } from 'react'
import BsButton from 'react-bootstrap/Button'

import { formatEasternDate } from 'helpers/dates'
import { useLabPermission } from 'hooks/useLabPermission'
import { useGetLabDeviceQuery } from 'store/services/devices'
import type { DeviceAccessory, DeviceAccessorySlot, DueAccessory } from 'store/types'
import { UserPermission } from '../user/permissions'
import { AccessoryModal } from './AccessoryModal'
import { AccessorySerialModal } from './AccessorySerialModal'

interface IProps {
  deviceId: number
}

const onSlot = (accessories: DeviceAccessory[] | null | undefined, slot: DeviceAccessorySlot) =>
  accessories?.find((item) => item.type === slot.type && Number(item.slot) === slot.slot) ?? null

// TES-3191: the probe, patch or position sensor a tracked device needs, each with its own count.
// Apart from the device's own fields; nothing for a device type that takes none.
export const DeviceAccessories = ({ deviceId }: IProps) => {
  const { data: device } = useGetLabDeviceQuery(deviceId)
  const canManageDevices = useLabPermission(UserPermission.MANAGE_DEVICES)
  const [installing, setInstalling] = useState<DeviceAccessorySlot | null>(null)
  const [editing, setEditing] = useState<(DeviceAccessory & { label: string }) | null>(null)

  const shelfLife = device?.shelfLife

  if (!device || !shelfLife?.requiredAccessories.length) return null

  const deviceName = device.serial || device.label || `#${device.id}`
  const missing = shelfLife.missingAccessories.map((slot: DeviceAccessorySlot) => slot.label)

  return (
    <div className="col-12 mb-4" data-testid="device-accessories">
      {/* card-title floats; the warning below would wrap around it */}
      <h5 className="card-title float-none mb-3">Accessories</h5>
      {missing.length > 0 && (
        <div className="alert alert-warning py-2" role="alert">
          This device cannot be checked out until its {missing.join(' and ')}{' '}
          {missing.length > 1 ? 'are' : 'is'} assigned.
        </div>
      )}
      <table className="table table-sm table-bordered">
        <thead className="thead-light">
          <tr>
            <th>Accessory</th>
            <th>Serial</th>
            <th>Installed</th>
            <th>Uses</th>
            {canManageDevices && <th />}
          </tr>
        </thead>
        <tbody>
          {shelfLife.requiredAccessories.map((slot: DeviceAccessorySlot) => {
            const accessory = onSlot(device.accessories, slot)
            const due = shelfLife.accessoriesDue.some(
              (item: DueAccessory) => item.type === slot.type && item.slot === slot.slot,
            )

            return (
              <tr key={`${slot.type}-${slot.slot}`} data-testid={`accessory-${slot.type}-${slot.slot}`}>
                <td>{slot.label}</td>
                <td>
                  {accessory ? accessory.serial || <span className="text-muted">—</span> : (
                    <span className="text-danger">Not assigned</span>
                  )}
                  {accessory && canManageDevices && (
                    <BsButton
                      size="sm"
                      variant="link"
                      className="py-0"
                      aria-label={`Edit ${slot.label} serial`}
                      title="Correct the serial; the use count stays"
                      onClick={() => setEditing({ ...accessory, label: slot.label })}>
                      <i className="fas fa-pen" />
                    </BsButton>
                  )}
                </td>
                <td>{accessory ? formatEasternDate(accessory.installedAt) : '—'}</td>
                <td className="text-nowrap">
                  {accessory ? (
                    <>
                      {accessory.uses}
                      {slot.replacementUses ? ` / ${slot.replacementUses}` : ''}
                    </>
                  ) : (
                    '—'
                  )}
                  {due && <span className="badge badge-warning ml-2">Due for replacement</span>}
                </td>
                {canManageDevices && (
                  <td className="text-right">
                    <BsButton size="sm" variant="outline-primary" onClick={() => setInstalling(slot)}>
                      {accessory ? 'Replace' : 'Assign'}
                    </BsButton>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>

      <AccessoryModal
        deviceId={Number(device.id)}
        deviceName={deviceName}
        slot={installing}
        current={installing ? onSlot(device.accessories, installing) : null}
        onClose={() => setInstalling(null)}
      />
      <AccessorySerialModal deviceId={Number(device.id)} accessory={editing} onClose={() => setEditing(null)} />
    </div>
  )
}
